import type { ThemeColors } from "./theme";

/**
 * Templates de temas predefinidos
 */

/**
 * Tema oscuro con rojo (tema energético y profesional)
 */
export const darkRedTheme: ThemeColors = {
  // Backgrounds - Slate oscuro neutro
  bgPrimary: "rgb(30, 41, 59)", // slate-800
  bgSecondary: "rgb(51, 65, 85)", // slate-700
  bgTertiary: "rgb(15, 23, 42)", // slate-900
  bgOverlay: "rgba(30, 41, 59, 0.9)", // slate-800/90
  bgOverlaySecondary: "rgba(51, 65, 85, 0.7)", // slate-700/70

  // Fondo de página - Gradiente slate neutro
  bgPageFrom: "rgb(15, 23, 42)", // slate-900
  bgPageVia: "rgb(30, 41, 59)", // slate-800
  bgPageTo: "rgb(51, 65, 85)", // slate-700

  // Botones - Rojo vibrante y profesional
  btnPrimary: "rgb(239, 68, 68)", // red-500 (más vibrante y profesional)
  btnPrimaryHover: "rgb(220, 38, 38)", // red-600
  btnPrimaryActive: "rgb(185, 28, 28)", // red-700
  btnPrimaryGradientFrom: "rgb(248, 113, 113)", // red-400
  btnPrimaryGradientVia: "rgb(239, 68, 68)", // red-500
  btnPrimaryGradientTo: "rgb(220, 38, 38)", // red-600
  btnPrimaryGradientHoverFrom: "rgb(239, 68, 68)", // red-500
  btnPrimaryGradientHoverVia: "rgb(220, 38, 38)", // red-600
  btnPrimaryGradientHoverTo: "rgb(185, 28, 28)", // red-700

  // Bordes - Slate con acento rojo sutil
  borderPrimary: "rgb(51, 65, 85)", // slate-700
  borderSecondary: "rgb(71, 85, 105)", // slate-600

  // Textos generales - Blanco y grises claros
  textPrimary: "rgb(248, 250, 252)", // slate-50
  textSecondary: "rgb(203, 213, 225)", // slate-300
  textTertiary: "rgb(148, 163, 184)", // slate-400
  textMuted: "rgb(100, 116, 139)", // slate-500
  textAccent: "rgb(248, 113, 113)", // red-400
  textPlaceholder: "rgb(148, 163, 184)", // slate-400

  // Títulos
  textTitle: "rgb(248, 250, 252)", // slate-50
  textTitleAccent: "rgb(248, 113, 113)", // red-400

  // Subtítulos
  textSubtitle: "rgb(203, 213, 225)", // slate-300

  // Labels
  textLabel: "rgb(203, 213, 225)", // slate-300

  // Sombras - Rojo profesional
  shadowPrimary:
    "0 10px 15px -3px rgba(239, 68, 68, 0.25), 0 4px 6px -2px rgba(239, 68, 68, 0.15)",
  shadowPrimaryHover:
    "0 20px 25px -5px rgba(239, 68, 68, 0.35), 0 10px 10px -5px rgba(239, 68, 68, 0.2)",
  shadowPrimaryLight:
    "0 4px 6px -1px rgba(239, 68, 68, 0.15), 0 2px 4px -1px rgba(239, 68, 68, 0.1)",
  shadowCard:
    "0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.25)",
  shadowCardHover:
    "0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 10px 20px -5px rgba(0, 0, 0, 0.35)",
  shadowButton:
    "0 10px 15px -3px rgba(239, 68, 68, 0.25), 0 4px 6px -2px rgba(239, 68, 68, 0.15)",

  // Estados y alertas - Colores profesionales
  colorFocusRing: "rgba(239, 68, 68, 0.5)", // red-500
  colorFocusBorder: "rgba(239, 68, 68, 0.6)", // red-500
  colorAccentOverlay: "rgba(239, 68, 68, 0.08)", // red-500
  colorAccentRing: "rgba(239, 68, 68, 0.25)", // red-500
  colorErrorBg: "rgba(239, 68, 68, 0.15)", // red-500
  colorErrorBorder: "rgba(239, 68, 68, 0.4)", // red-500
  colorErrorText: "rgb(252, 165, 165)", // red-300
  colorWarningBg: "rgba(251, 191, 36, 0.15)", // amber-400
  colorWarningBorder: "rgba(251, 191, 36, 0.4)", // amber-400
  colorWarningText: "rgb(251, 191, 36)", // amber-400
  colorInfoBg: "rgba(59, 130, 246, 0.15)", // blue-500
  colorInfoBorder: "rgba(59, 130, 246, 0.4)", // blue-500
  colorInfoText: "rgb(147, 197, 253)", // blue-300
  colorSuccessBg: "rgba(34, 197, 94, 0.15)", // green-500
  colorSuccessBorder: "rgba(34, 197, 94, 0.4)", // green-500
  colorSuccessText: "rgb(134, 239, 172)", // green-300
  colorOperationalExpense: "rgb(245, 158, 11)", // amber-500
  colorOperationalExpenseBg: "rgba(245, 158, 11, 0.1)", // amber-500
  colorOperationalExpenseBorder: "rgba(245, 158, 11, 0.3)", // amber-500
  colorNonOperationalExpense: "rgb(249, 115, 22)", // orange-500
  colorNonOperationalExpenseBg: "rgba(249, 115, 22, 0.1)", // orange-500
  colorNonOperationalExpenseBorder: "rgba(249, 115, 22, 0.3)", // orange-500
  colorCheckbox: "rgb(239, 68, 68)", // red-500
  colorToggleInactive: "rgb(71, 85, 105)", // slate-600 - visible en fondos oscuros
  colorToggleInactiveHover: "rgb(100, 116, 139)", // slate-500 - hover más claro

  // Color para iconos SVG en headers (sidebar) - Rojo para coincidir con el tema
  sidebarIconColor: "rgb(239, 68, 68)", // red-500

  // Constelaciones
  constellationDot: "200, 200, 220",
  constellationLine: "180, 180, 200",
  constellationDotOpacity: "0.5",
  constellationLineOpacity: "0.15",
};

/**
 * Tema claro con verde (tema natural y fresco)
 */
export const lightGreenTheme: ThemeColors = {
  // Backgrounds - Superficies blancas que se despegan del fondo gris de página
  bgPrimary: "rgb(255, 255, 255)", // white
  bgSecondary: "rgb(241, 245, 249)", // slate-100
  bgTertiary: "rgb(226, 232, 240)", // slate-200
  bgOverlay: "rgba(255, 255, 255, 0.95)", // white/95
  bgOverlaySecondary: "rgba(241, 245, 249, 0.92)", // slate-100/92

  // Fondo de página - Gris que da contraste a las superficies blancas
  bgPageFrom: "rgb(241, 245, 249)", // slate-100
  bgPageVia: "rgb(233, 238, 245)",
  bgPageTo: "rgb(226, 232, 240)", // slate-200

  // Botones - Verde oscurecido para que el texto blanco sea legible
  btnPrimary: "rgb(22, 163, 74)", // green-600
  btnPrimaryHover: "rgb(21, 128, 61)", // green-700
  btnPrimaryActive: "rgb(22, 101, 52)", // green-800
  btnPrimaryGradientFrom: "rgb(34, 197, 94)", // green-500
  btnPrimaryGradientVia: "rgb(22, 163, 74)", // green-600
  btnPrimaryGradientTo: "rgb(21, 128, 61)", // green-700
  btnPrimaryGradientHoverFrom: "rgb(22, 163, 74)", // green-600
  btnPrimaryGradientHoverVia: "rgb(21, 128, 61)", // green-700
  btnPrimaryGradientHoverTo: "rgb(22, 101, 52)", // green-800

  // Bordes - Grises visibles sobre superficie blanca
  borderPrimary: "rgb(203, 213, 225)", // slate-300
  borderSecondary: "rgb(148, 163, 184)", // slate-400

  // Textos generales - Grises oscuros profesionales
  textPrimary: "rgb(15, 23, 42)", // slate-900
  textSecondary: "rgb(51, 65, 85)", // slate-700
  textTertiary: "rgb(71, 85, 105)", // slate-600
  textMuted: "rgb(100, 116, 139)", // slate-500
  textAccent: "rgb(21, 128, 61)", // green-700
  textPlaceholder: "rgb(148, 163, 184)", // slate-400

  // Títulos
  textTitle: "rgb(15, 23, 42)", // slate-900
  textTitleAccent: "rgb(21, 128, 61)", // green-700

  // Subtítulos
  textSubtitle: "rgb(51, 65, 85)", // slate-700

  // Labels
  textLabel: "rgb(51, 65, 85)", // slate-700

  // Sombras - Suaves y profesionales
  shadowPrimary:
    "0 10px 15px -3px rgba(34, 197, 94, 0.2), 0 4px 6px -2px rgba(34, 197, 94, 0.1)",
  shadowPrimaryHover:
    "0 20px 25px -5px rgba(34, 197, 94, 0.3), 0 10px 10px -5px rgba(34, 197, 94, 0.15)",
  shadowPrimaryLight:
    "0 4px 6px -1px rgba(34, 197, 94, 0.1), 0 2px 4px -1px rgba(34, 197, 94, 0.06)",
  shadowCard:
    "0 1px 2px rgba(15, 23, 42, 0.06), 0 4px 10px -2px rgba(15, 23, 42, 0.1)",
  shadowCardHover:
    "0 2px 4px rgba(15, 23, 42, 0.08), 0 12px 20px -6px rgba(15, 23, 42, 0.18)",
  shadowButton:
    "0 4px 6px -1px rgba(34, 197, 94, 0.2), 0 2px 4px -1px rgba(34, 197, 94, 0.1)",

  // Estados y alertas - Colores profesionales
  colorFocusRing: "rgba(34, 197, 94, 0.5)", // green-500
  colorFocusBorder: "rgba(34, 197, 94, 0.5)", // green-500
  colorAccentOverlay: "rgba(34, 197, 94, 0.05)", // green-500
  colorAccentRing: "rgba(34, 197, 94, 0.2)", // green-500
  colorErrorBg: "rgba(239, 68, 68, 0.1)", // red-500
  colorErrorBorder: "rgba(239, 68, 68, 0.3)", // red-500
  colorErrorText: "rgb(185, 28, 28)", // red-700
  colorWarningBg: "rgba(251, 191, 36, 0.15)", // amber-400
  colorWarningBorder: "rgba(251, 191, 36, 0.4)", // amber-400
  colorWarningText: "rgb(161, 123, 5)", // dorado oscuro (contraste sobre fondo claro)
  colorInfoBg: "rgba(59, 130, 246, 0.1)", // blue-500
  colorInfoBorder: "rgba(59, 130, 246, 0.3)", // blue-500
  colorInfoText: "rgb(29, 78, 216)", // blue-700
  colorSuccessBg: "rgba(34, 197, 94, 0.1)", // green-500
  colorSuccessBorder: "rgba(34, 197, 94, 0.3)", // green-500
  colorSuccessText: "rgb(22, 101, 52)", // green-700
  colorOperationalExpense: "rgb(245, 158, 11)", // amber-500
  colorOperationalExpenseBg: "rgba(245, 158, 11, 0.1)", // amber-500
  colorOperationalExpenseBorder: "rgba(245, 158, 11, 0.3)", // amber-500
  colorNonOperationalExpense: "rgb(249, 115, 22)", // orange-500
  colorNonOperationalExpenseBg: "rgba(249, 115, 22, 0.1)", // orange-500
  colorNonOperationalExpenseBorder: "rgba(249, 115, 22, 0.3)", // orange-500
  colorCheckbox: "rgb(34, 197, 94)", // green-500
  colorToggleInactive: "rgb(203, 213, 225)", // slate-300 - visible en fondos claros
  colorToggleInactiveHover: "rgb(148, 163, 184)", // slate-400 - hover más oscuro

  // Color para iconos SVG en headers (sidebar) - Verde para coincidir con el tema
  sidebarIconColor: "rgb(21, 128, 61)", // green-700

  // Constelaciones (tonos oscuros para fondo claro)
  constellationDot: "80, 90, 110",
  constellationLine: "100, 110, 130",
  constellationDotOpacity: "0.35",
  constellationLineOpacity: "0.1",
};

/**
 * Tema oscuro con azul (tema profesional moderno)
 */
export const darkBlueTheme: ThemeColors = {
  // Backgrounds - Slate oscuro con toques azules sutiles
  bgPrimary: "rgb(30, 41, 59)", // slate-800 (neutro y profesional)
  bgSecondary: "rgb(51, 65, 85)", // slate-700
  bgTertiary: "rgb(15, 23, 42)", // slate-900
  bgOverlay: "rgba(30, 41, 59, 0.9)", // slate-800/90
  bgOverlaySecondary: "rgba(51, 65, 85, 0.7)", // slate-700/70

  // Fondo de página - Gradiente sutil slate-azul
  bgPageFrom: "rgb(15, 23, 42)", // slate-900
  bgPageVia: "rgb(30, 41, 59)", // slate-800
  bgPageTo: "rgb(51, 65, 85)", // slate-700

  // Botones - Azul profesional vibrante
  btnPrimary: "rgb(59, 130, 246)", // blue-500 (profesional y vibrante)
  btnPrimaryHover: "rgb(37, 99, 235)", // blue-600
  btnPrimaryActive: "rgb(29, 78, 216)", // blue-700
  btnPrimaryGradientFrom: "rgb(96, 165, 250)", // blue-400
  btnPrimaryGradientVia: "rgb(59, 130, 246)", // blue-500
  btnPrimaryGradientTo: "rgb(37, 99, 235)", // blue-600
  btnPrimaryGradientHoverFrom: "rgb(59, 130, 246)", // blue-500
  btnPrimaryGradientHoverVia: "rgb(37, 99, 235)", // blue-600
  btnPrimaryGradientHoverTo: "rgb(29, 78, 216)", // blue-700

  // Bordes - Slate con acento azul sutil
  borderPrimary: "rgb(51, 65, 85)", // slate-700
  borderSecondary: "rgb(71, 85, 105)", // slate-600

  // Textos generales - Blanco y grises claros con buen contraste
  textPrimary: "rgb(248, 250, 252)", // slate-50 (blanco suave)
  textSecondary: "rgb(203, 213, 225)", // slate-300
  textTertiary: "rgb(148, 163, 184)", // slate-400
  textMuted: "rgb(100, 116, 139)", // slate-500
  textAccent: "rgb(96, 165, 250)", // blue-400 (acento suave)
  textPlaceholder: "rgb(148, 163, 184)", // slate-400

  // Títulos
  textTitle: "rgb(248, 250, 252)", // slate-50
  textTitleAccent: "rgb(96, 165, 250)", // blue-400

  // Subtítulos
  textSubtitle: "rgb(203, 213, 225)", // slate-300

  // Labels
  textLabel: "rgb(203, 213, 225)", // slate-300

  // Sombras - Azul profesional con profundidad
  shadowPrimary:
    "0 10px 15px -3px rgba(59, 130, 246, 0.25), 0 4px 6px -2px rgba(59, 130, 246, 0.15)",
  shadowPrimaryHover:
    "0 20px 25px -5px rgba(59, 130, 246, 0.35), 0 10px 10px -5px rgba(59, 130, 246, 0.2)",
  shadowPrimaryLight:
    "0 4px 6px -1px rgba(59, 130, 246, 0.15), 0 2px 4px -1px rgba(59, 130, 246, 0.1)",
  shadowCard:
    "0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.25)",
  shadowCardHover:
    "0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 10px 20px -5px rgba(0, 0, 0, 0.35)",
  shadowButton:
    "0 10px 15px -3px rgba(59, 130, 246, 0.25), 0 4px 6px -2px rgba(59, 130, 246, 0.15)",

  // Estados y alertas - Colores profesionales
  colorFocusRing: "rgba(59, 130, 246, 0.5)", // blue-500
  colorFocusBorder: "rgba(59, 130, 246, 0.6)", // blue-500
  colorAccentOverlay: "rgba(59, 130, 246, 0.08)", // blue-500
  colorAccentRing: "rgba(59, 130, 246, 0.25)", // blue-500
  colorErrorBg: "rgba(239, 68, 68, 0.15)", // red-500
  colorErrorBorder: "rgba(239, 68, 68, 0.4)", // red-500
  colorErrorText: "rgb(252, 165, 165)", // red-300
  colorWarningBg: "rgba(251, 191, 36, 0.15)", // amber-400
  colorWarningBorder: "rgba(251, 191, 36, 0.4)", // amber-400
  colorWarningText: "rgb(251, 191, 36)", // amber-400
  colorInfoBg: "rgba(59, 130, 246, 0.15)", // blue-500
  colorInfoBorder: "rgba(59, 130, 246, 0.4)", // blue-500
  colorInfoText: "rgb(147, 197, 253)", // blue-300
  colorSuccessBg: "rgba(34, 197, 94, 0.15)", // green-500
  colorSuccessBorder: "rgba(34, 197, 94, 0.4)", // green-500
  colorSuccessText: "rgb(134, 239, 172)", // green-300
  colorOperationalExpense: "rgb(245, 158, 11)", // amber-500
  colorOperationalExpenseBg: "rgba(245, 158, 11, 0.1)", // amber-500
  colorOperationalExpenseBorder: "rgba(245, 158, 11, 0.3)", // amber-500
  colorNonOperationalExpense: "rgb(249, 115, 22)", // orange-500
  colorNonOperationalExpenseBg: "rgba(249, 115, 22, 0.1)", // orange-500
  colorNonOperationalExpenseBorder: "rgba(249, 115, 22, 0.3)", // orange-500
  colorCheckbox: "rgb(59, 130, 246)", // blue-500
  colorToggleInactive: "rgb(71, 85, 105)", // slate-600 - visible en fondos oscuros
  colorToggleInactiveHover: "rgb(100, 116, 139)", // slate-500 - hover más claro

  // Constelaciones
  constellationDot: "200, 200, 220",
  constellationLine: "180, 180, 200",
  constellationDotOpacity: "0.5",
  constellationLineOpacity: "0.15",
};

export const lightBlueTheme: ThemeColors = {
  // Backgrounds - Superficies blancas que se despegan del fondo gris de página
  bgPrimary: "rgb(255, 255, 255)", // white (limpio y profesional)
  bgSecondary: "rgb(241, 245, 249)", // slate-100
  bgTertiary: "rgb(226, 232, 240)", // slate-200
  bgOverlay: "rgba(255, 255, 255, 0.95)", // white/95
  bgOverlaySecondary: "rgba(241, 245, 249, 0.92)", // slate-100/92

  // Fondo de página - Gris que da contraste a las superficies blancas
  bgPageFrom: "rgb(241, 245, 249)", // slate-100
  bgPageVia: "rgb(233, 238, 245)",
  bgPageTo: "rgb(226, 232, 240)", // slate-200

  // Botones - Azul oscurecido para que el texto blanco sea legible
  btnPrimary: "rgb(37, 99, 235)", // blue-600
  btnPrimaryHover: "rgb(29, 78, 216)", // blue-700
  btnPrimaryActive: "rgb(30, 64, 175)", // blue-800
  btnPrimaryGradientFrom: "rgb(59, 130, 246)", // blue-500
  btnPrimaryGradientVia: "rgb(37, 99, 235)", // blue-600
  btnPrimaryGradientTo: "rgb(29, 78, 216)", // blue-700
  btnPrimaryGradientHoverFrom: "rgb(37, 99, 235)", // blue-600
  btnPrimaryGradientHoverVia: "rgb(29, 78, 216)", // blue-700
  btnPrimaryGradientHoverTo: "rgb(30, 64, 175)", // blue-800

  // Bordes - Grises visibles sobre superficie blanca
  borderPrimary: "rgb(203, 213, 225)", // slate-300
  borderSecondary: "rgb(148, 163, 184)", // slate-400

  // Textos generales - Grises oscuros profesionales
  textPrimary: "rgb(15, 23, 42)", // slate-900 (máximo contraste)
  textSecondary: "rgb(51, 65, 85)", // slate-700
  textTertiary: "rgb(71, 85, 105)", // slate-600
  textMuted: "rgb(100, 116, 139)", // slate-500
  textAccent: "rgb(29, 78, 216)", // blue-700
  textPlaceholder: "rgb(148, 163, 184)", // slate-400

  // Títulos
  textTitle: "rgb(15, 23, 42)", // slate-900
  textTitleAccent: "rgb(29, 78, 216)", // blue-700

  // Subtítulos
  textSubtitle: "rgb(51, 65, 85)", // slate-700

  // Labels
  textLabel: "rgb(51, 65, 85)", // slate-700

  // Sombras - Suaves y profesionales
  shadowPrimary:
    "0 10px 15px -3px rgba(59, 130, 246, 0.2), 0 4px 6px -2px rgba(59, 130, 246, 0.1)",
  shadowPrimaryHover:
    "0 20px 25px -5px rgba(59, 130, 246, 0.3), 0 10px 10px -5px rgba(59, 130, 246, 0.15)",
  shadowPrimaryLight:
    "0 4px 6px -1px rgba(59, 130, 246, 0.1), 0 2px 4px -1px rgba(59, 130, 246, 0.06)",
  shadowCard:
    "0 1px 2px rgba(15, 23, 42, 0.06), 0 4px 10px -2px rgba(15, 23, 42, 0.1)",
  shadowCardHover:
    "0 2px 4px rgba(15, 23, 42, 0.08), 0 12px 20px -6px rgba(15, 23, 42, 0.18)",
  shadowButton:
    "0 4px 6px -1px rgba(59, 130, 246, 0.2), 0 2px 4px -1px rgba(59, 130, 246, 0.1)",

  // Estados y alertas - Colores profesionales
  colorFocusRing: "rgba(59, 130, 246, 0.5)", // blue-500
  colorFocusBorder: "rgba(59, 130, 246, 0.5)", // blue-500
  colorAccentOverlay: "rgba(59, 130, 246, 0.05)", // blue-500
  colorAccentRing: "rgba(59, 130, 246, 0.2)", // blue-500
  colorErrorBg: "rgba(239, 68, 68, 0.1)", // red-500
  colorErrorBorder: "rgba(239, 68, 68, 0.3)", // red-500
  colorErrorText: "rgb(185, 28, 28)", // red-700
  colorWarningBg: "rgba(251, 191, 36, 0.15)", // amber-400
  colorWarningBorder: "rgba(251, 191, 36, 0.4)", // amber-400
  colorWarningText: "rgb(161, 123, 5)", // dorado oscuro (contraste sobre fondo claro)
  colorInfoBg: "rgba(59, 130, 246, 0.1)", // blue-500
  colorInfoBorder: "rgba(59, 130, 246, 0.3)", // blue-500
  colorInfoText: "rgb(29, 78, 216)", // blue-700
  colorSuccessBg: "rgba(34, 197, 94, 0.1)", // green-500
  colorSuccessBorder: "rgba(34, 197, 94, 0.3)", // green-500
  colorSuccessText: "rgb(22, 101, 52)", // green-700
  colorOperationalExpense: "rgb(245, 158, 11)", // amber-500
  colorOperationalExpenseBg: "rgba(245, 158, 11, 0.1)", // amber-500
  colorOperationalExpenseBorder: "rgba(245, 158, 11, 0.3)", // amber-500
  colorNonOperationalExpense: "rgb(249, 115, 22)", // orange-500
  colorNonOperationalExpenseBg: "rgba(249, 115, 22, 0.1)", // orange-500
  colorNonOperationalExpenseBorder: "rgba(249, 115, 22, 0.3)", // orange-500
  colorCheckbox: "rgb(59, 130, 246)", // blue-500
  colorToggleInactive: "rgb(203, 213, 225)", // slate-300 - visible en fondos claros
  colorToggleInactiveHover: "rgb(148, 163, 184)", // slate-400 - hover más oscuro

  // Color para iconos SVG en headers (sidebar) - Azul para coincidir con el tema
  sidebarIconColor: "rgb(29, 78, 216)", // blue-700

  // Constelaciones (tonos oscuros para fondo claro)
  constellationDot: "80, 90, 110",
  constellationLine: "100, 110, 130",
  constellationDotOpacity: "0.35",
  constellationLineOpacity: "0.1",
};

export const lightYellowTheme: ThemeColors = {
  // Backgrounds - Superficies blancas que se despegan del fondo gris de página
  bgPrimary: "rgb(255, 255, 255)", // white
  bgSecondary: "rgb(241, 245, 249)", // slate-100
  bgTertiary: "rgb(226, 232, 240)", // slate-200
  bgOverlay: "rgba(255, 255, 255, 0.95)", // white/95
  bgOverlaySecondary: "rgba(241, 245, 249, 0.92)", // slate-100/92

  // Fondo de página - Gris que da contraste a las superficies blancas
  bgPageFrom: "rgb(241, 245, 249)", // slate-100
  bgPageVia: "rgb(233, 238, 245)",
  bgPageTo: "rgb(226, 232, 240)", // slate-200

  // Botones - Dorado oscurecido para que el texto blanco sea legible
  // ponytail: se oscurece el dorado en vez de agregar --btn-primary-on y tocar
  // los ~20 text-white sobre accent; si se quiere el dorado claro, esa es la vía.
  btnPrimary: "rgb(166, 124, 0)", // dorado oscuro
  btnPrimaryHover: "rgb(140, 104, 0)", // darker
  btnPrimaryActive: "rgb(120, 90, 0)", // darkest
  btnPrimaryGradientFrom: "rgb(184, 148, 18)", // lighter
  btnPrimaryGradientVia: "rgb(166, 124, 0)", // dorado oscuro
  btnPrimaryGradientTo: "rgb(140, 104, 0)", // darker
  btnPrimaryGradientHoverFrom: "rgb(166, 124, 0)", // dorado oscuro
  btnPrimaryGradientHoverVia: "rgb(140, 104, 0)", // darker
  btnPrimaryGradientHoverTo: "rgb(120, 90, 0)", // darkest

  // Bordes - Grises visibles sobre superficie blanca
  borderPrimary: "rgb(203, 213, 225)", // slate-300
  borderSecondary: "rgb(148, 163, 184)", // slate-400

  // Textos generales - Grises oscuros profesionales
  textPrimary: "rgb(15, 23, 42)", // slate-900
  textSecondary: "rgb(51, 65, 85)", // slate-700
  textTertiary: "rgb(71, 85, 105)", // slate-600
  textMuted: "rgb(100, 116, 139)", // slate-500
  textAccent: "rgb(161, 123, 5)", // dorado oscuro para contraste en fondo claro
  textPlaceholder: "rgb(148, 163, 184)", // slate-400

  // Títulos
  textTitle: "rgb(15, 23, 42)", // slate-900
  textTitleAccent: "rgb(161, 123, 5)", // dorado oscuro

  // Subtítulos
  textSubtitle: "rgb(51, 65, 85)", // slate-700

  // Labels
  textLabel: "rgb(51, 65, 85)", // slate-700

  // Sombras - Con tinte dorado
  shadowPrimary:
    "0 10px 15px -3px rgba(245, 179, 5, 0.2), 0 4px 6px -2px rgba(245, 179, 5, 0.1)",
  shadowPrimaryHover:
    "0 20px 25px -5px rgba(245, 179, 5, 0.3), 0 10px 10px -5px rgba(245, 179, 5, 0.15)",
  shadowPrimaryLight:
    "0 4px 6px -1px rgba(245, 179, 5, 0.1), 0 2px 4px -1px rgba(245, 179, 5, 0.06)",
  shadowCard:
    "0 1px 2px rgba(15, 23, 42, 0.06), 0 4px 10px -2px rgba(15, 23, 42, 0.1)",
  shadowCardHover:
    "0 2px 4px rgba(15, 23, 42, 0.08), 0 12px 20px -6px rgba(15, 23, 42, 0.18)",
  shadowButton:
    "0 4px 6px -1px rgba(245, 179, 5, 0.2), 0 2px 4px -1px rgba(245, 179, 5, 0.1)",

  // Estados y alertas
  colorFocusRing: "rgba(245, 179, 5, 0.5)",
  colorFocusBorder: "rgba(245, 179, 5, 0.5)",
  colorAccentOverlay: "rgba(245, 179, 5, 0.08)",
  colorAccentRing: "rgba(245, 179, 5, 0.2)",
  colorErrorBg: "rgba(239, 68, 68, 0.1)", // red-500
  colorErrorBorder: "rgba(239, 68, 68, 0.3)", // red-500
  colorErrorText: "rgb(185, 28, 28)", // red-700
  colorWarningBg: "rgba(251, 191, 36, 0.15)", // amber-400
  colorWarningBorder: "rgba(251, 191, 36, 0.4)", // amber-400
  colorWarningText: "rgb(161, 123, 5)", // dorado oscuro
  colorInfoBg: "rgba(245, 179, 5, 0.1)",
  colorInfoBorder: "rgba(245, 179, 5, 0.3)",
  colorInfoText: "rgb(161, 123, 5)", // dorado oscuro
  colorSuccessBg: "rgba(34, 197, 94, 0.1)", // green-500
  colorSuccessBorder: "rgba(34, 197, 94, 0.3)", // green-500
  colorSuccessText: "rgb(22, 101, 52)", // green-700
  colorOperationalExpense: "rgb(245, 158, 11)", // amber-500
  colorOperationalExpenseBg: "rgba(245, 158, 11, 0.1)",
  colorOperationalExpenseBorder: "rgba(245, 158, 11, 0.3)",
  colorNonOperationalExpense: "rgb(249, 115, 22)", // orange-500
  colorNonOperationalExpenseBg: "rgba(249, 115, 22, 0.1)",
  colorNonOperationalExpenseBorder: "rgba(249, 115, 22, 0.3)",
  colorCheckbox: "rgb(245, 179, 5)",
  colorToggleInactive: "rgb(203, 213, 225)", // slate-300 - visible en fondos claros
  colorToggleInactiveHover: "rgb(148, 163, 184)", // slate-400

  // Color para iconos SVG en headers (sidebar)
  sidebarIconColor: "rgb(161, 123, 5)", // dorado oscuro

  // Constelaciones (tonos dorados suaves para fondo claro)
  constellationDot: "180, 140, 10",
  constellationLine: "200, 160, 20",
  constellationDotOpacity: "0.3",
  constellationLineOpacity: "0.08",
};

export const darkYellowTheme: ThemeColors = {
  bgPrimary: "rgb(15, 15,15)",
  bgSecondary: "rgb(25, 25, 25)",
  bgTertiary: "rgb(10, 10, 10)",
  bgOverlay: "rgba(15, 15, 15, 0.9)",
  bgOverlaySecondary: "rgba(25, 25, 25, 0.7)",

  // Fondo de página - Gradiente negro elegante
  bgPageFrom: "rgb(10, 10, 10)",
  bgPageVia: "rgb(15, 15, 15)",
  bgPageTo: "rgb(25, 25, 25)",

  // Botones - Amarillo #f5b305
  btnPrimary: "rgb(245, 179, 5)", // #f5b305
  btnPrimaryHover: "rgb(221, 161, 5)", // darker
  btnPrimaryActive: "rgb(196, 143, 4)", // darkest
  btnPrimaryGradientFrom: "rgb(251, 193, 28)", // lighter
  btnPrimaryGradientVia: "rgb(245, 179, 5)", // #f5b305
  btnPrimaryGradientTo: "rgb(221, 161, 5)", // darker
  btnPrimaryGradientHoverFrom: "rgb(245, 179, 5)", // #f5b305
  btnPrimaryGradientHoverVia: "rgb(221, 161, 5)", // darker
  btnPrimaryGradientHoverTo: "rgb(196, 143, 4)", // darkest

  // Bordes - Negro con acento dorado sutil
  borderPrimary: "rgb(35, 35, 35)",
  borderSecondary: "rgb(45, 45, 45)",

  // Textos generales
  textPrimary: "rgb(255, 255, 255)",
  textSecondary: "rgb(229, 231, 235)", // gray-200
  textTertiary: "rgb(209, 213, 219)", // gray-300
  textMuted: "rgb(156, 163, 175)", // gray-400
  textAccent: "rgb(245, 179, 5)", // #f5b305
  textPlaceholder: "rgb(156, 163, 175)", // gray-400

  // Títulos
  textTitle: "rgb(255, 255, 255)",
  textTitleAccent: "rgb(245, 179, 5)", // #f5b305

  // Subtítulos
  textSubtitle: "rgb(229, 231, 235)", // gray-200

  // Labels
  textLabel: "rgb(229, 231, 235)", // gray-200

  // Sombras - Amarillo #f5b305
  shadowPrimary:
    "0 10px 15px -3px rgba(245, 179, 5, 0.3), 0 4px 6px -2px rgba(245, 179, 5, 0.2)",
  shadowPrimaryHover:
    "0 20px 25px -5px rgba(245, 179, 5, 0.4), 0 10px 10px -5px rgba(245, 179, 5, 0.25)",
  shadowPrimaryLight:
    "0 4px 6px -1px rgba(245, 179, 5, 0.2), 0 2px 4px -1px rgba(245, 179, 5, 0.15)",
  shadowCard:
    "0 20px 25px -5px rgba(0, 0, 0, 0.6), 0 10px 10px -5px rgba(0, 0, 0, 0.4)",
  shadowCardHover:
    "0 25px 50px -12px rgba(0, 0, 0, 0.7), 0 10px 20px -5px rgba(0, 0, 0, 0.5)",
  shadowButton:
    "0 10px 15px -3px rgba(245, 179, 5, 0.3), 0 4px 6px -2px rgba(245, 179, 5, 0.2)",

  // Estados y alertas
  colorFocusRing: "rgba(245, 179, 5, 0.5)",
  colorFocusBorder: "rgba(245, 179, 5, 0.6)",
  colorAccentOverlay: "rgba(245, 179, 5, 0.1)",
  colorAccentRing: "rgba(245, 179, 5, 0.3)",
  colorErrorBg: "rgba(239, 68, 68, 0.15)",
  colorErrorBorder: "rgba(239, 68, 68, 0.4)",
  colorErrorText: "rgb(252, 165, 165)", // red-300
  colorWarningBg: "rgba(245, 179, 5, 0.15)",
  colorWarningBorder: "rgba(245, 179, 5, 0.4)",
  colorWarningText: "rgb(245, 179, 5)",
  colorInfoBg: "rgba(59, 130, 246, 0.15)",
  colorInfoBorder: "rgba(59, 130, 246, 0.4)",
  colorInfoText: "rgb(147, 197, 253)", // blue-300
  colorSuccessBg: "rgba(34, 197, 94, 0.15)",
  colorSuccessBorder: "rgba(34, 197, 94, 0.4)",
  colorSuccessText: "rgb(134, 239, 172)", // green-300
  colorOperationalExpense: "rgb(245, 158, 11)", // amber-500
  colorOperationalExpenseBg: "rgba(245, 158, 11, 0.1)",
  colorOperationalExpenseBorder: "rgba(245, 158, 11, 0.3)",
  colorNonOperationalExpense: "rgb(249, 115, 22)", // orange-500
  colorNonOperationalExpenseBg: "rgba(249, 115, 22, 0.1)",
  colorNonOperationalExpenseBorder: "rgba(249, 115, 22, 0.3)",
  colorCheckbox: "rgb(245, 179, 5)",
  colorToggleInactive: "rgb(45, 45, 45)",
  colorToggleInactiveHover: "rgb(55, 55, 55)",

  // Color para iconos SVG en headers (sidebar)
  sidebarIconColor: "rgb(245, 179, 5)",

  // Constelaciones - Tonos amarillos #f5b305
  constellationDot: "251, 193, 28",
  constellationLine: "245, 179, 5",
  constellationDotOpacity: "0.45",
  constellationLineOpacity: "0.12",
};

/**
 * Mapa de templates disponibles.
 * dark_yellow es el tema por defecto y se muestra primero en el selector de perfil.
 */
export const themeTemplates = {
  dark_yellow: {
    name: "Negro con Dorado",
    colors: darkYellowTheme,
  },
  dark_red: {
    name: "Oscuro con Rojo",
    colors: darkRedTheme,
  },
  light_green: {
    name: "Claro con Verde",
    colors: lightGreenTheme,
  },
  dark_blue: {
    name: "Oscuro con Azul",
    colors: darkBlueTheme,
  },
  light_blue: {
    name: "Claro con Azul",
    colors: lightBlueTheme,
  },
  light_yellow: {
    name: "Blanco con Dorado",
    colors: lightYellowTheme,
  },
} as const;

export type ThemeTemplateKey = keyof typeof themeTemplates;
